import { __createBinding } from 'tslib';
import { apolloClient } from '~/apollo-client';
import { MINNESOTA_LMI_FUNNEL_ID } from '~/funnels/minnesota-lmi/config';
import { ParsedQueryParams, UnparsedQueryParams } from '~/providers/query-params/types';
import {
  EnrollingUserInput,
  Flex_CreateEnrollingUserMutation,
  Flex_CreateEnrollingUserMutationVariables,
  OriginationSource,
} from '~/typings/graphql';
import { errors } from '~/utils/error-messages';
import { determineOriginationSource, determineLanguage } from '~/utils/funnels';
import { store } from '~/utils/store';
import { sendToSegment } from '~/utils/tracking';
import { CREATE_ENROLLING_USER } from './create-user.queries';

// The ABP program in Illinois has different requirements than Illinois's non block program.
// We are going to manage non-block projects soon and are building a new funnel to support non ABP use cases.
// For now, any time we have a user signup in an Illinois utility, they should be marked as ABP.
const abpProjectGroupId = 1;
const nonAbpProjectGroupId = 2;
const oregonTier1ProjectGroupId = 35;
const oregonTier1LmiProjectGroupId = 35;
const oregonTier2ProjectGroupId = 36;
const oregonTier2LmiProjectGroupId = 36;
const minnesotaLmiProjectGroupId = 102;
const minnesotaLegacyProjectGroupId = 135;

const illinoisUtilityIds = [2269, 2441];
const oregonUtilityIds = [2477, 2352];
const mnUtilityIds = [2447]; // Xcel MN

export const createUser = async (params: CreateUserParams): Promise<CreateUserResponse> => {
  const processedUnparsedQueryParams = processUnparsedQueryParams(
    params.unparsedQueryParams || {},
    params.email,
  );

  const input: EnrollingUserInput = {
    budgetBilling: !!params.budgetBilling,
    businessName: params.businessName,
    contractVersionIds: params.generalContractIds,
    communitySolarProjectGroupId: determineProjectGroup(params.utilityId, params.funnelId),
    customerType: params.businessName ? 'smb' : undefined,
    email: params.email,
    firstName: params.firstName,
    funnel: params.funnel || params.funnelId,
    inviteCode: params.inviteCode ? String(params.inviteCode) : undefined,
    ipAddress: params.ipAddress,
    languageShortCode: determineLanguage(),
    lastName: params.lastName,
    funnelVnmProjectId: params.funnelVnmProjectId,
    originationSourceType: params.originationSource || determineOriginationSource(params),
    password: params.password,
    phone: params.phone,
    smsOptIn: !!params.smsOptIn,
    stripeCustomerId: params.stripeCustomerId,
    stripePaymentId: params.stripePaymentId,
    planTemplateId: params.planTemplateId,
    queryParams: processedUnparsedQueryParams,
    referrableCode: params.promoCode,
    utilityId: params.utilityId,
    zip: params.zipCode,
    metaData: params.metaData,
  };

  const response = await apolloClient.mutate<
    Flex_CreateEnrollingUserMutation,
    Flex_CreateEnrollingUserMutationVariables
  >({
    mutation: CREATE_ENROLLING_USER,
    variables: { input },
  });

  const userId = response.data?.enrollingUser.user?.id;
  if (!response.data || !userId) throw new Error(errors.missingCreateAccountResponse);

  store.set('TOKEN', response.data.enrollingUser.token!);
  store.set('USER_ID', userId);

  return { userId };
};

const determineProjectGroup = (utilityId: number, funnelId: string | undefined) => {
  if (illinoisUtilityIds.includes(utilityId)) {
    if (funnelId === 'nonABP') {
      return nonAbpProjectGroupId;
    } else return abpProjectGroupId;
  }
  if (oregonUtilityIds.includes(utilityId)) {
    if (funnelId === 'oregonTier1') return oregonTier1ProjectGroupId;
    if (funnelId === 'oregonTier2') return oregonTier2ProjectGroupId;
    if (funnelId === 'oregonTier1Lmi') return oregonTier1LmiProjectGroupId;
    if (funnelId === 'oregonTier2Lmi') return oregonTier2LmiProjectGroupId;
  }
  if (mnUtilityIds.includes(utilityId)) {
    if (funnelId === MINNESOTA_LMI_FUNNEL_ID) return minnesotaLmiProjectGroupId;
    return minnesotaLegacyProjectGroupId; // For all other funnels
  }
  return null;
};

export interface CreateUserParams {
  budgetBilling?: boolean;
  businessName?: string;
  communitySolarProjectGroupId?: number;
  email: string;
  firstName: string;
  funnelId?: string;
  funnelVnmProjectId?: number;
  generalContractIds?: number[];
  inviteCode?: string;
  ipAddress?: string;
  lastName: string;
  originationSource?: OriginationSource;
  password?: string;
  phone?: string;
  smsOptIn?: boolean;
  planTemplateId: number;
  promoCode?: string;
  stripeCustomerId?: string;
  stripePaymentId?: string;
  unparsedQueryParams: ParsedQueryParams['unparsedQueryParams'];
  utilityId: number;
  funnelTrack?: string;
  funnel?: string;
  utmParams?: Record<string, string | number>;
  zipCode: string;
  metaData?: string;
}

export interface CreateUserResponse {
  userId: number;
}

export const processUnparsedQueryParams = (
  unparsedQueryParams: UnparsedQueryParams,
  email: string,
): { utilityAccountNumber?: string; secondaryAccountId?: string } => {
  // Do not change the decodingMap
  const decodeStringtoNumber = (
    encodedNumber: string,
    email: string,
    queryParam: string,
  ): string | undefined => {
    const decodingMap: Record<string, string> = {
      r: '0',
      m: '1',
      b: '2',
      j: '3',
      p: '4',
      t: '5',
      c: '6',
      g: '7',
      f: '8',
      w: '9',
    };

    let decodedNumber = '';
    let letter;

    for (letter of encodedNumber) {
      if (decodingMap.hasOwnProperty(letter)) {
        decodedNumber += decodingMap[letter];
      } else {
        sendToSegment({
          type: 'track_queryParams_decoding_error',
          encodedNumber,
          queryParam,
          email,
        });
        return undefined;
      }
    }
    return decodedNumber;
  };

  const utilityAccountNumber = unparsedQueryParams.encodedUtilityAccountNumber
    ? decodeStringtoNumber(
        unparsedQueryParams.encodedUtilityAccountNumber,
        email,
        'utilityAccountNumber',
      )
    : undefined;

  const secondaryAccountId = unparsedQueryParams.encodedSecondaryAccountId
    ? decodeStringtoNumber(
        unparsedQueryParams.encodedSecondaryAccountId,
        email,
        'secondaryAccountId',
      )
    : undefined;

  return { ...unparsedQueryParams, utilityAccountNumber, secondaryAccountId };
};
